import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BuyContractService } from '../buy-contract.service';
import { BuyContract } from '../buy-contract.type';

interface PatchBuyContractDialogData {
  contract: BuyContract;
}

interface PatchBuyContractForm {
  enableName: FormControl<boolean>;
  name: FormControl<string>;
  enableNote: FormControl<boolean>;
  note: FormControl<string>;
  enableTargetCoins: FormControl<boolean>;
  targetCoins: FormControl<number | null>;
}

@Component({
  selector: 'app-patch-buy-contract-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatIconModule,
  ],
  templateUrl: './patch-buy-contract-dialog.html',
  styleUrl: './patch-buy-contract-dialog.scss',
})
export class PatchBuyContractDialog implements OnInit {
  private fb = inject(FormBuilder);
  private dialogRef = inject(MatDialogRef<PatchBuyContractDialog>);
  private service = inject(BuyContractService);
  private snackBar = inject(MatSnackBar);
  readonly data = inject<PatchBuyContractDialogData>(MAT_DIALOG_DATA);

  loading = false;

  patchForm: FormGroup<PatchBuyContractForm> = this.fb.nonNullable.group({
    enableName: [false],
    name: [this.data.contract.name ?? ''],

    enableNote: [false],
    note: [this.data.contract.note ?? ''],

    enableTargetCoins: [false],
    targetCoins: [this.data.contract.target_coins as number | null]
  });

  ngOnInit(): void {
    this.setupConditionalValidation();
  }

  private setupConditionalValidation(): void {
    this.patchForm.controls.enableName.valueChanges.subscribe(enabled => {
      const ctrl = this.patchForm.controls.name;
      if (!enabled) ctrl.setValue(this.data.contract.name ?? '');
      ctrl.updateValueAndValidity();
    });

    this.patchForm.controls.enableNote.valueChanges.subscribe(enabled => {
      const ctrl = this.patchForm.controls.note;
      if (!enabled) ctrl.setValue(this.data.contract.note ?? '');
      ctrl.updateValueAndValidity();
    });

    this.patchForm.controls.enableTargetCoins.valueChanges.subscribe(enabled => {
      const ctrl = this.patchForm.controls.targetCoins;
      if (enabled) {
        ctrl.setValidators([Validators.required, Validators.min(1)]);
      } else {
        ctrl.clearValidators();
        ctrl.setValue(this.data.contract.target_coins);
      }
      ctrl.updateValueAndValidity();
    });
  }

  isNameEnabled(): boolean { return this.patchForm.controls.enableName.value; }
  isNoteEnabled(): boolean { return this.patchForm.controls.enableNote.value; }
  isTargetCoinsEnabled(): boolean { return this.patchForm.controls.enableTargetCoins.value; }

  onSubmit(): void {
    if (this.patchForm.invalid) {
      this.patchForm.markAllAsTouched();
      return;
    }

    const anyEnabled = this.isNameEnabled() || this.isNoteEnabled() || this.isTargetCoinsEnabled();
    if (!anyEnabled) {
      this.snackBar.open('Enable at least one field to update', 'Close', { duration: 3000 });
      return;
    }

    this.loading = true;

    this.service.patchBuyContract(this.data.contract.id, {
      name: this.isNameEnabled() ? this.patchForm.controls.name.value : null,
      note: this.isNoteEnabled() ? this.patchForm.controls.note.value : null,
      target_coins: this.isTargetCoinsEnabled() ? this.patchForm.controls.targetCoins.value : null,
    }).subscribe({
      next: () => {
        this.snackBar.open('Contract updated successfully', 'Close', { duration: 5000 });
        this.dialogRef.close(true);
      },
      error: (error) => {
        const msg = error.error?.error || 'Error updating contract';
        this.snackBar.open(msg, 'Close', { duration: 5000 });
        this.loading = false;
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
